import React, { useState } from 'react';
import { FolderOpen, ExternalLink, Play, Check, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { projects } from '../mock';
import { useTheme } from '../context/ThemeContext';

const Projects = () => {
  // Use object to track each cell's state independently
  const [cellStates, setCellStates] = useState({});
  const { theme } = useTheme();

  const handleRunCell = (cellId) => {
    setCellStates(prev => ({
      ...prev,
      [cellId]: { running: true, completed: false }
    }));
    
    setTimeout(() => {
      setCellStates(prev => ({
        ...prev,
        [cellId]: { running: false, completed: true }
      }));
    }, 1500);
  };

  const toggleCell = (cellId) => {
    setCellStates(prev => ({
      ...prev,
      [cellId]: { 
        ...prev[cellId],
        completed: !prev[cellId]?.completed 
      }
    }));
  };

  const getCellState = (cellId) => ({
    running: cellStates[cellId]?.running || false,
    completed: cellStates[cellId]?.completed || false
  });

  return (
    <section id="projects" className={`py-12 sm:py-20 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#0f1f26] to-[#1B3139]' 
        : 'bg-gradient-to-br from-gray-100 to-white'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <FolderOpen className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            <span 
              className="text-[#FF3621] font-semibold text-xs sm:text-sm uppercase tracking-wider"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Portfolio
            </span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Featured Projects
          </h2>
          <p 
            className={`text-base sm:text-lg max-w-2xl mx-auto px-4 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Run cells to explore enterprise-scale data and AI solutions
          </p>
        </div>

        <div className="space-y-4 sm:space-y-8">
          {projects.map((project, index) => {
            const cellId = `project-${project.id}`;
            const state = getCellState(cellId);

            return (
              <div 
                key={project.id}
                className={`border rounded-lg overflow-hidden shadow-xl transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'bg-[#1B3139] border-white/10 hover:border-[#FF3621]/50' 
                    : 'bg-white border-gray-200 hover:border-[#FF3621]/50'
                }`}
              >
                {/* Cell Header */}
                <div className={`flex items-center justify-between px-3 sm:px-4 py-2 border-b ${
                  theme === 'dark' 
                    ? 'bg-white/5 border-white/10' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <span 
                    className={`text-xs font-mono truncate flex-1 ${
                      theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                    }`}
                    style={{ fontFamily: 'DM Mono, monospace' }}
                  >
                    Cell [{index + 10}]: {project.title}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span 
                      className={`text-xs font-mono hidden sm:inline ${
                        theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      {project.year}
                    </span>
                    {state.completed && (
                      <button 
                        onClick={() => toggleCell(cellId)}
                        className={`p-1 rounded transition-colors ${
                          theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-200'
                        }`}
                      >
                        <ChevronUp className={`h-4 w-4 ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="relative">
                  {/* Run Button */}
                  <div className={`absolute left-0 top-0 bottom-0 w-10 sm:w-12 border-r flex items-start justify-center pt-4 sm:pt-6 ${
                    theme === 'dark' 
                      ? 'bg-white/5 border-white/10' 
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <Button
                      onClick={() => handleRunCell(cellId)}
                      disabled={state.running}
                      className="bg-transparent hover:bg-[#FF3621]/20 border border-[#FF3621]/50 text-[#FF3621] p-1.5 sm:p-2 h-7 w-7 sm:h-8 sm:w-8 rounded transition-all duration-300"
                    >
                      {state.running ? (
                        <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-[#FF3621] border-t-transparent rounded-full"></div>
                      ) : state.completed ? (
                        <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                      ) : (
                        <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Code Preview */}
                  <div className="pl-12 sm:pl-16 pr-4 sm:pr-6 py-4 sm:py-6">
                    <pre 
                      className={`text-xs sm:text-sm font-mono mb-4 overflow-x-auto ${
                        theme === 'dark' ? 'text-white/80' : 'text-gray-700'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      project = {'{'}<br />
                      {'  '}"title": "{project.title}",<br />
                      {'  '}"client": "{project.client}",<br />
                      <span className="hidden sm:inline">{'  '}"technologies": {JSON.stringify(project.technologies.slice(0, 3))}...<br /></span>
                      <span className="sm:hidden">{'  '}"tech": [...{project.technologies.length} items]<br /></span>
                      {'}'}
                    </pre>

                    {!state.completed && (
                      <p 
                        className={`text-xs sm:text-sm ${
                          theme === 'dark' ? 'text-white/60' : 'text-gray-500'
                        }`}
                        style={{ fontFamily: 'DM Sans, sans-serif' }}
                      >
                        Click run to see project details...
                      </p>
                    )}
                  </div>
                </div>

                {/* Output - Project Details */}
                {state.completed && (
                  <div className={`border-t ${
                    theme === 'dark' 
                      ? 'border-white/10 bg-black/30' 
                      : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="px-4 sm:px-6 py-4 sm:py-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                        <div>
                          <h3 
                            className={`text-xl sm:text-2xl font-bold mb-2 ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                            style={{ fontFamily: 'DM Sans, sans-serif' }}
                          >
                            {project.title}
                          </h3>
                          <p 
                            className="text-[#FF3621] font-medium mb-2 sm:mb-4 text-sm sm:text-base"
                            style={{ fontFamily: 'DM Sans, sans-serif' }}
                          >
                            {project.client}
                          </p>
                        </div>
                        <span 
                          className={`text-xs font-mono sm:hidden mb-2 ${
                            theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                          }`}
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          {project.year}
                        </span>
                      </div>

                      <p 
                        className={`mb-4 leading-relaxed text-sm sm:text-base ${
                          theme === 'dark' ? 'text-white/70' : 'text-gray-600'
                        }`}
                        style={{ fontFamily: 'DM Sans, sans-serif' }}
                      >
                        {project.description}
                      </p>

                      <div className="flex items-center space-x-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span 
                            className="text-xs sm:text-sm text-green-500 font-medium"
                            style={{ fontFamily: 'DM Sans, sans-serif' }}
                          >
                            Impact: {project.impact}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {project.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-2 sm:px-3 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-500 rounded text-xs sm:text-sm font-medium"
                            style={{ fontFamily: 'DM Mono, monospace' }}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Projects;
